import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReloadDock } from '@components/ui/ReloadDock/ReloadDock';
import type { IToast } from '@components/ui/Toasts/useToasts';
import { TruckIcon } from '@utils/icons';
import type { ILogisticsEditLock } from './LogisticsEditLock';
import { useLogisticsRevision } from './useLogisticsRevision';

interface IUseLogisticsReloadArgs {
  siteId: string;
  /** The revision on the page's `['logistics', siteId]` payload. */
  loadedRevision: number | undefined;
  /** That query's `isFetchedAfterMount`. */
  fetchedAfterMount: boolean;
  canEdit: boolean;
  /** A form is open and may hold input (an order or stock dialog, or a drawer
   *  whose Edit tab was opened): a refresh says the input was kept. */
  formOpen: boolean;
  push: (toast: Omit<IToast, 'id'>) => void;
}

/**
 * The page's refresh-to-edit wiring in one place: the revision state, the
 * "Load" action, the dock that offers it, and the edit-lock context value.
 */
export function useLogisticsReload({
  siteId,
  loadedRevision,
  fetchedAfterMount,
  canEdit,
  formOpen,
  push,
}: IUseLogisticsReloadArgs) {
  const { t } = useTranslation();
  const { revision, ready, stale, reloading, reload } = useLogisticsRevision({
    siteId,
    loadedRevision,
    fetchedAfterMount,
    enabled: canEdit,
  });
  /** "Load": refetch everything, then lift the lock. A form left open keeps
   *  its input, so say so; a failed reload keeps the lock. */
  const loadLatest = async () => {
    try {
      await reload();
    } catch {
      push({ kind: 'error', title: t('logistics.error') });
      return;
    }
    if (formOpen) push({ kind: 'success', title: t('logistics.stale.reloaded'), sub: t('logistics.stale.reloadedSub') });
  };
  /* Nothing is editable until the page's own first fetch settled the revision. */
  const locked = stale || !ready;
  const editLock: ILogisticsEditLock = useMemo(
    () => ({ revision, saveLocked: stale, locked }),
    [revision, stale, locked],
  );
  return {
    revision,
    stale,
    editLock,
    dock: stale ? (
      <ReloadDock
        icon={<TruckIcon size={20} />}
        title={t('logistics.stale.title')}
        sub={t('logistics.stale.sub')}
        busy={reloading}
        onReload={() => void loadLatest()}
      />
    ) : null,
  };
}
